// frontend/src/hooks/games/useMemoryGame.ts

import { useCallback, useEffect, useMemo, useState } from 'react';
import { EMOJI_POOL, shuffle } from '../../components/games/memory/emojiPool';

export type MemoryDifficulty = 'easy' | 'medium' | 'hard';

export interface MemoryCardModel {
  id: number;
  emoji: string;
  isFlipped: boolean;
  isMatched: boolean;
}

export interface MemoryDifficultyConfig {
  label: string;
  gridSize: number;
}

export const MEMORY_DIFFICULTIES: Record<MemoryDifficulty, MemoryDifficultyConfig> = {
  easy: { label: 'Kolay', gridSize: 4 },
  medium: { label: 'Orta', gridSize: 6 },
  hard: { label: 'Zor', gridSize: 8 },
};

const MISMATCH_DELAY_MS = 800;

function buildBoard(difficulty: MemoryDifficulty): MemoryCardModel[] {
  const { gridSize } = MEMORY_DIFFICULTIES[difficulty];
  const totalCards = gridSize * gridSize;
  const pairCount = totalCards / 2;

  // Havuzdan rastgele, tekrarsız emoji seç (havuz her zaman en büyük ihtiyaçtan büyük tutulur).
  const chosenEmojis = shuffle(EMOJI_POOL).slice(0, pairCount);
  const deck = shuffle([...chosenEmojis, ...chosenEmojis]);

  return deck.map((emoji, index) => ({
    id: index,
    emoji,
    isFlipped: false,
    isMatched: false,
  }));
}

interface FlippedEntry {
  id: number;
  emoji: string;
}

export function useMemoryGame(initialDifficulty: MemoryDifficulty = 'easy') {
  const [difficulty, setDifficulty] = useState<MemoryDifficulty>(initialDifficulty);
  const [cards, setCards] = useState<MemoryCardModel[]>(() => buildBoard(initialDifficulty));
  const [flipped, setFlipped] = useState<FlippedEntry[]>([]);
  const [isLocked, setIsLocked] = useState(false);
  const [moves, setMoves] = useState(0);

  const totalPairs = cards.length / 2;
  const matchedPairs = useMemo(() => cards.filter((c) => c.isMatched).length / 2, [cards]);
  const isComplete = totalPairs > 0 && matchedPairs === totalPairs;

  const startNewGame = useCallback((nextDifficulty?: MemoryDifficulty) => {
    const targetDifficulty = nextDifficulty ?? difficulty;
    setDifficulty(targetDifficulty);
    setCards(buildBoard(targetDifficulty));
    setFlipped([]);
    setIsLocked(false);
    setMoves(0);
    // NOT: İleride buraya round başlangıcı için XP/istatistik event'i eklenebilir.
  }, [difficulty]);

  const flipCard = useCallback((card: MemoryCardModel) => {
    if (isLocked) return;
    if (card.isFlipped || card.isMatched) return;
    if (flipped.length >= 2) return;

    setCards((prev) => prev.map((c) => (c.id === card.id ? { ...c, isFlipped: true } : c)));
    setFlipped((prev) => [...prev, { id: card.id, emoji: card.emoji }]);
  }, [isLocked, flipped]);

  // İki kart açıldığında eşleşme kontrolü
  useEffect(() => {
    if (flipped.length !== 2) return;

    const [first, second] = flipped;
    setMoves((m) => m + 1);

    if (first.emoji === second.emoji) {
      // Eşleşme: kartlar açık kalır, gecikme yok.
      setCards((prev) =>
        prev.map((c) => (c.id === first.id || c.id === second.id ? { ...c, isMatched: true } : c))
      );
      setFlipped([]);
      // NOT: İleride buraya "eşleşme bulundu" XP/istatistik event'i eklenebilir.
    } else {
      // Eşleşmedi: kısa bir gecikmeden sonra kartları geri kapat.
      setIsLocked(true);
      const timer = setTimeout(() => {
        setCards((prev) =>
          prev.map((c) => (c.id === first.id || c.id === second.id ? { ...c, isFlipped: false } : c))
        );
        setFlipped([]);
        setIsLocked(false);
      }, MISMATCH_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [flipped]);

  // NOT: Oyun tamamlandığında ileride buraya XP/başarım/istatistik event'i eklenebilir.
  // useEffect(() => { if (isComplete) { /* onGameComplete?.({ difficulty, moves }) */ } }, [isComplete]);

  return {
    difficulty,
    gridSize: MEMORY_DIFFICULTIES[difficulty].gridSize,
    cards,
    moves,
    matchedPairs,
    totalPairs,
    isComplete,
    flipCard,
    startNewGame,
  };
}
